import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Map, Trophy, Gift, CheckCircle, Lock, Star, Crown, Gem, User } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MILESTONE_ICONS = {
  5: '🌟',
  10: '🍺',
  15: '🦉',
  20: '🏆',
  30: '👑',
  50: '💎'
};

const PassportProgress = ({ passport, onClaimReward, user }) => {
  const { unique_bars_visited, next_milestone, progress_percent, rewards_earned } = passport;
  
  // Get profile picture URL
  const getProfilePicUrl = () => {
    if (user?.profile_picture_url) {
      return user.profile_picture_url.startsWith('/api') 
        ? `${BACKEND_URL}${user.profile_picture_url}` 
        : user.profile_picture_url;
    }
    return null;
  };
  
  return (
    <Card className="glass p-6 border border-slate-700">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          {/* User Profile Picture */}
          <div className="relative">
            {getProfilePicUrl() ? (
              <img 
                src={getProfilePicUrl()}
                alt={user?.username || 'User'}
                className="w-14 h-14 rounded-full object-cover border-2 border-purple-500"
              />
            ) : (
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center border-2 border-purple-500">
                <span className="text-white text-xl font-bold">
                  {user?.username?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
            )}
            <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center">
              <Map className="w-3 h-3 text-white" />
            </div>
          </div>
          <div>
            <h3 className="text-xl font-bold text-white">Bar Passport</h3>
            <p className="text-sm text-slate-400">{user?.username || 'Explorer'}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-3xl font-bold text-purple-400">{unique_bars_visited}</p>
          <p className="text-xs text-slate-400">Bars Visited</p>
        </div>
      </div>

      {/* Progress to next milestone */}
      {next_milestone && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-300">
              Next: {MILESTONE_ICONS[next_milestone.milestone]} {next_milestone.name}
            </span>
            <span className="text-sm text-purple-400">
              {next_milestone.bars_needed} more bars
            </span>
          </div>
          <Progress value={progress_percent} className="h-3 bg-slate-700" />
          <p className="text-xs text-slate-400 mt-1">
            Reward: {next_milestone.reward}
          </p>
        </div>
      )}

      {/* Earned Rewards */}
      {rewards_earned && rewards_earned.length > 0 && (
        <div className="border-t border-slate-700 pt-4">
          <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <Trophy className="w-4 h-4 text-yellow-400" />
            Rewards Earned
          </h4>
          <div className="flex flex-wrap gap-2">
            {rewards_earned.map(reward => (
              <Badge 
                key={reward.milestone}
                className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 text-yellow-300 border-yellow-500/50"
              >
                {reward.icon} {reward.name}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};

const PassportMilestones = ({ passport, onClaimReward }) => {
  const { unique_bars_visited, rewards_earned } = passport;
  const claimedMilestones = rewards_earned?.map(r => r.milestone) || [];
  
  const milestones = [
    { bars: 5, name: 'Rookie Explorer', icon: '🌟', reward: '50 bonus points' },
    { bars: 10, name: 'Bar Hopper', icon: '🍺', reward: '100 bonus points + Badge' },
    { bars: 15, name: 'Night Owl', icon: '🦉', reward: '150 bonus points' },
    { bars: 20, name: 'Bar Connoisseur', icon: '🏆', reward: 'Free T-Shirt + 200 points' },
    { bars: 30, name: 'Legend', icon: '👑', reward: 'VIP Status + 500 points' },
    { bars: 50, name: 'Bar Buzz Elite', icon: '💎', reward: 'Lifetime Premium + 1000 points' }
  ];

  return (
    <Card className="glass p-6 border border-slate-700">
      <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <Gift className="w-5 h-5 text-purple-400" />
        Milestones & Rewards
      </h3>
      <div className="space-y-3">
        {milestones.map(milestone => {
          const isUnlocked = unique_bars_visited >= milestone.bars;
          const isClaimed = claimedMilestones.includes(milestone.bars);
          
          return (
            <div 
              key={milestone.bars}
              className={`p-3 rounded-lg border ${
                isUnlocked 
                  ? 'bg-green-500/10 border-green-500/50' 
                  : 'bg-slate-800/50 border-slate-700'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{milestone.icon}</span>
                  <div>
                    <p className={`font-semibold ${isUnlocked ? 'text-green-400' : 'text-slate-400'}`}>
                      {milestone.name}
                    </p>
                    <p className="text-xs text-slate-400">
                      Visit {milestone.bars} bars • {milestone.reward}
                    </p>
                  </div>
                </div>
                <div>
                  {isClaimed ? (
                    <Badge className="bg-green-500/20 text-green-400">
                      <CheckCircle className="w-3 h-3 mr-1" /> Claimed
                    </Badge>
                  ) : isUnlocked ? (
                    <Button 
                      size="sm"
                      onClick={() => onClaimReward(milestone.bars)}
                      className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                    >
                      Claim!
                    </Button>
                  ) : (
                    <Badge variant="outline" className="text-slate-500 border-slate-600">
                      <Lock className="w-3 h-3 mr-1" /> {milestone.bars - unique_bars_visited} to go
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

const VisitedBars = ({ bars }) => {
  if (!bars || bars.length === 0) {
    return (
      <Card className="glass p-6 border border-slate-700">
        <div className="text-center py-8">
          <Map className="w-12 h-12 mx-auto text-slate-600 mb-3" />
          <p className="text-slate-400">No bars visited yet</p>
          <p className="text-sm text-slate-500">Start exploring to fill your passport!</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="glass p-6 border border-slate-700">
      <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <CheckCircle className="w-5 h-5 text-green-400" />
        Bars Visited ({bars.length})
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-64 overflow-y-auto">
        {bars.map(bar => (
          <div 
            key={bar.id}
            className="p-3 bg-slate-800/50 rounded-lg border border-slate-700"
          >
            <p className="text-sm text-white font-medium truncate">{bar.name}</p>
            <p className="text-xs text-slate-400 truncate">{bar.address}</p>
          </div>
        ))}
      </div>
    </Card>
  );
};

const BarPassport = () => {
  const { user } = useContext(AuthContext);
  const [passport, setPassport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFull, setShowFull] = useState(false);

  const fetchPassport = async () => {
    try {
      const response = await axios.get(`${API}/passport`);
      setPassport(response.data);
    } catch (error) {
      console.error('Failed to fetch passport:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPassport();
  }, []);

  const handleClaimReward = async (milestone) => {
    try {
      const response = await axios.post(`${API}/passport/claim-reward/${milestone}`);
      toast.success(`🎉 ${response.data.message}`, {
        description: `You earned ${response.data.reward.icon} ${response.data.reward.name}!`
      });
      fetchPassport(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to claim reward');
    }
  };

  if (loading) {
    return (
      <Card className="glass p-6 border border-slate-700 animate-pulse">
        <div className="h-32 bg-slate-700 rounded"></div>
      </Card>
    );
  }

  if (!passport) {
    return null;
  }

  return (
    <div className="space-y-4">
      <PassportProgress passport={passport} onClaimReward={handleClaimReward} user={user} />
      
      <Dialog open={showFull} onOpenChange={setShowFull}>
        <DialogTrigger asChild>
          <Button 
            variant="outline" 
            className="w-full border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
          >
            <Map className="w-4 h-4 mr-2" />
            View Full Passport
          </Button>
        </DialogTrigger>
        <DialogContent className="glass border border-slate-700 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Map className="w-5 h-5 text-purple-400" />
              Your Bar Passport
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <PassportProgress passport={passport} onClaimReward={handleClaimReward} user={user} />
            <PassportMilestones passport={passport} onClaimReward={handleClaimReward} />
            <VisitedBars bars={passport.visited_bars} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BarPassport;
